import logger from "../utils/logger.js";
import { sanitizeError, formatErrorForUI } from "../utils/errorSanitizer.js";

const API_URL = process.env.REACT_APP_API_URL || "/laravel-api/api";
const REQUEST_TIMEOUT_MS = 15000; // 15 seconds
const MAX_RETRIES = 2;
const INITIAL_RETRY_DELAY_MS = 300; // 300ms

// Centralized auth token getter (used by other modules)
export function getAuthToken() {
  // Token is stored in localStorage for API header usage
  // httpOnly cookies are an alternative but currently using localStorage
  try {
    const token = localStorage.getItem("mg_token");
    return token || "";
  } catch (e) {
    console.error("Failed to retrieve auth token:", e);
    return "";
  }
}

function normalizeEndpoint(endpoint) {
  const ep = String(endpoint || "").trim();
  if (/^https?:\/\//i.test(ep)) return ep;
  const cleaned = ep.replace(/^\/+/, "").replace(/^api\/+/i, "");
  return `${API_URL}/${cleaned}`;
}

async function safeParseResponse(res) {
  const contentType = res.headers.get("content-type") || "";
  const text = await res.text();

  if (!text || text.trim() === "") {
    return { _raw: "", _contentType: contentType };
  }

  const lower = text.trim().toLowerCase();
  const looksLikeHtml =
    lower.startsWith("<!doctype") ||
    lower.startsWith("<html") ||
    lower.includes("<body") ||
    lower.includes("</html>");

  if (looksLikeHtml) {
    throw {
      status: res.status,
      message: "Server returned HTML instead of JSON",
      raw: text,
      contentType,
    };
  }

  try {
    const json = JSON.parse(text);
    if (json && typeof json === "object") {
      json._raw = text;
      json._contentType = contentType;
      json._http_status = res.status;
    }
    return json;
  } catch (e) {
    logger.error("Invalid JSON from server:", text);
    throw {
      status: res.status,
      message: "Invalid JSON response from server",
      raw: text,
      contentType,
    };
  }
}

const memoryCache = new Map();

function getFromCache(key, ttlMs) {
  const entry = memoryCache.get(key);
  if (!entry) return null;

  if (Date.now() - entry.time > ttlMs) {
    memoryCache.delete(key);
    return null;
  }

  return entry.data;
}

function saveToCache(key, data) {
  memoryCache.set(key, { data, time: Date.now() });
}

// Track in-flight GET requests for deduplication
const inFlightRequests = new Map();

async function sleep(ms) {
  return new Promise(resolve => setTimeout(resolve, ms));
}

async function safeFetchJsonWithRetry(url, fetchOptions, method) {
  // Check if this is a GET request and already in flight
  if (method === 'GET') {
    const key = `${method}:${url}`;
    if (inFlightRequests.has(key)) {
      return inFlightRequests.get(key);
    }
  }

  let lastError = null;

  for (let attempt = 0; attempt <= MAX_RETRIES; attempt++) {
    try {
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), REQUEST_TIMEOUT_MS);

      const res = await fetch(url, {
        ...fetchOptions,
        signal: controller.signal,
      });

      clearTimeout(timeoutId);

      let data;
      try {
        data = await safeParseResponse(res);
      } catch (parseErr) {
        throw {
          status: parseErr.status ?? res.status,
          message: parseErr.message || "Response parse error",
          body: { raw: parseErr.raw, contentType: parseErr.contentType },
        };
      }

      if (!res.ok) {
        // Don't retry on 4xx errors (client errors)
        if (res.status >= 400 && res.status < 500) {
          throw {
            status: res.status,
            message: data?.message || "Request failed",
            body: data,
          };
        }

        // For POST/PUT/DELETE methods, never retry on 5xx errors
        // (they modify data and retrying causes duplicates)
        if (method !== 'GET') {
          throw {
            status: res.status,
            message: data?.message || "Request failed",
            body: data,
          };
        }

        // For GET requests on 5xx errors, retry if attempts remain
        if (attempt < MAX_RETRIES) {
          const delayMs = INITIAL_RETRY_DELAY_MS * Math.pow(2, attempt);
          await sleep(delayMs);
          continue;
        }

        // Last attempt or 5xx on GET without retries left
        throw {
          status: res.status,
          message: data?.message || "Request failed",
          body: data,
        };
      }

      return data;
    } catch (err) {
      lastError = err;

      // Don't retry on AbortError if it's not a timeout
      if (err.name === 'AbortError' && err.message === 'The user aborted a request.') {
        throw { status: 0, message: "Request timeout after " + REQUEST_TIMEOUT_MS + "ms", error: err };
      }

      // Don't retry on client errors
      if (lastError.status && lastError.status >= 400 && lastError.status < 500) {
        throw lastError;
      }

      // For POST/PUT/DELETE, only retry on network errors (status 0), not server errors
      if (method !== 'GET' && lastError.status && lastError.status >= 500) {
        throw lastError;
      }

      if (attempt < MAX_RETRIES) {
        const delayMs = INITIAL_RETRY_DELAY_MS * Math.pow(2, attempt);
        await sleep(delayMs);
      }
    }
  }

  // After all retries failed
  if (lastError) {
    throw lastError;
  }

  throw { status: 0, message: "Request failed after retries", error: new Error("Unknown error") };
}

async function safeFetchJson(url, fetchOptions) {
  const method = fetchOptions.method || 'GET';
  return safeFetchJsonWithRetry(url, fetchOptions, method);
}

const api = {
 buildUrl(endpoint) {
 return normalizeEndpoint(endpoint);
 },

 async apiGet(endpoint, options = {}) {
 const { cache = false, ttl = 0, authToken = "" } = options;
 const url = normalizeEndpoint(endpoint);
 const cacheKey = `GET:${url}`;

 if (cache && ttl > 0) {
 const cached = getFromCache(cacheKey, ttl);
 if (cached) return cached;
 }

 const token = authToken || getAuthToken();
 const data = await safeFetchJson(url, {
 method: "GET",
 headers: {
 Accept: "application/json",
 ...(token ? { Authorization: `Bearer ${token}` } : {}),
 },
 credentials: "include",
 });

 if (cache && ttl > 0) saveToCache(cacheKey, data);
 return data;
 },

 async apiPostJson(endpoint, payload = {}) {
 const url = normalizeEndpoint(endpoint);
 const token = getAuthToken();
 return safeFetchJson(url, {
 method: "POST",
 headers: {
 "Content-Type": "application/json",
 Accept: "application/json",
 ...(token ? { Authorization: `Bearer ${token}` } : {}),
 },
 credentials: "include",
 body: JSON.stringify(payload),
 });
 },

 async apiPostForm(endpoint, payload = {}) {
 const url = normalizeEndpoint(endpoint);
 const token = getAuthToken();

 const body =
 payload instanceof URLSearchParams
 ? payload.toString()
 : new URLSearchParams(payload).toString();

 return safeFetchJson(url, {
 method: "POST",
 headers: {
 "Content-Type": "application/x-www-form-urlencoded;charset=UTF-8",
 Accept: "application/json",
 ...(token ? { Authorization: `Bearer ${token}` } : {}),
 },
 credentials: "include",
 body,
 });
 },

 async apiPostFormData(endpoint, formData) {
 const url = normalizeEndpoint(endpoint);
 const token = getAuthToken();
 return safeFetchJson(url, {
 method: "POST",
 headers: {
 Accept: "application/json",
 ...(token ? { Authorization: `Bearer ${token}` } : {}),
 },
 credentials: "include",
 body: formData,
 });
 },

 async apiPost(endpoint, data = {}) {
 if (data instanceof FormData) return api.apiPostFormData(endpoint, data);
 return api.apiPostJson(endpoint, data);
 },

 async apiDelete(endpoint) {
 const url = normalizeEndpoint(endpoint);
 const token = getAuthToken();
 return safeFetchJson(url, {
 method: "DELETE",
 headers: {
 Accept: "application/json",
 ...(token ? { Authorization: `Bearer ${token}` } : {}),
 },
 credentials: "include",
 });
 },

 async apiPut(endpoint, payload = {}) {
 const url = normalizeEndpoint(endpoint);
 const token = getAuthToken();
 return safeFetchJson(url, {
 method: "PUT",
 headers: {
 "Content-Type": "application/json",
 Accept: "application/json",
 ...(token ? { Authorization: `Bearer ${token}` } : {}),
 },
 credentials: "include",
 body: JSON.stringify(payload),
 });
 },

 async get(endpoint, options = {}) {
 return api.apiGet(endpoint, options);
 },

 async post(endpoint, data = {}) {
 return api.apiPost(endpoint, data);
 },

 async delete(endpoint) {
 return api.apiDelete(endpoint);
 },

 async put(endpoint, data = {}) {
 return api.apiPut(endpoint, data);
 },

 /**
  * Handle API errors with sanitization
  * Returns safe error message for user display
  */
 handleError(error) {
   if (!error) {
     return {
       message: 'An error occurred. Please try again.',
       fieldErrors: null,
     };
   }

   // Sanitize the error
   const sanitized = sanitizeError(
     error.status || error.http_status || 400,
     error.message || 'An error occurred',
     error.errors || error.body?.errors
   );

   // Log raw error in development
   if (process.env.NODE_ENV === 'development') {
     logger.error('API Error:', {
       status: sanitized.status,
       message: sanitized.originalMessage,
       errors: error.errors,
     });
   }

   // Return safe error for UI
   return formatErrorForUI({
     status: sanitized.status,
     message: sanitized.message,
     fieldErrors: sanitized.fieldErrors,
   });
 },
};

export default api;
